import React, { useState, useEffect, useRef } from 'react'
import { Box } from '@mui/material'
import SEOMetaTags from '@/components/SEOMetaTags'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { PageTransition } from '@/components/PageTransition'
import { DonateHeroSection } from '@/components/sections/DonateHeroSection'
import { EmotionalNarrativeSection } from '@/components/sections/EmotionalNarrativeSection'
import { FundAllocationSection } from '@/components/sections/FundAllocationSection'
import { ContributionTiersSection } from '@/components/sections/ContributionTiersSection'
import { DonateCtaSection } from '@/components/sections/DonateCtaSection'
import DonateDialog from '@/components/DonateDialog'
import { donatePageContent } from '@/data/donateContent'
import { getActiveTiers } from '@/data/contributionTiers'
import { trackEvent, trackPageView } from '@/utils/analytics'
import { getSEOConfig, getCanonicalUrl } from '@/utils/seo'
import { donationContent } from '@/data'
import type { DonatePageState } from '@/types/donate'

/**
 * Donation landing page component
 * Displays emotional narrative, contribution tiers, and donation CTA
 * Integrates with Zeffy donation platform via DonateDialog
 */
export const Donate: React.FC = () => {
  // Ref for scrolling to contribution tiers section
  const contributionTiersRef = useRef<HTMLElement>(null)

  // Page state management
  const [pageState, setPageState] = useState<DonatePageState>({
    isDonateDialogOpen: false,
    selectedTierId: undefined,
    isLoading: false,
    error: undefined,
    lastInteractedTier: undefined,
    donationSource: 'page_load', // Track where donation originated
  })

  // SEO configuration
  const seoConfig = getSEOConfig('/donate')!
  const canonicalUrl = getCanonicalUrl('/donate')

  // Filter tiers to only show active ones (not expired)
  const activeTiers = getActiveTiers()

  /**
   * Track page view on component mount
   */
  useEffect(() => {
    trackPageView('/donate', seoConfig.title)
    trackEvent('donate_page_viewed', {
      active_tiers: activeTiers.length,
    })
  }, [activeTiers.length, seoConfig.title])

  /**
   * Handle tier card click - track analytics and open dialog
   */
  const handleTierClick = (tierId: string) => {
    setPageState(prev => ({
      ...prev,
      isDonateDialogOpen: true,
      selectedTierId: tierId,
      lastInteractedTier: tierId,
      donationSource: 'tier_card',
    }))

    // Track tier selection event
    trackEvent('tier_selected', {
      tier_id: tierId,
      source: 'tier_card',
    })

    // Track dialog open
    trackEvent('donate_dialog_opened', {
      source: 'tier_card',
      tier_id: tierId,
    })
  }

  /**
   * Handle tier card hover/view - track analytics for engagement
   */
  const handleTierView = (tierId: string) => {
    trackEvent('tier_viewed', {
      tier_id: tierId,
      source: 'tier_card',
    })
  }

  /**
   * Handle final CTA button click - open donation dialog
   */
  const handleCtaClick = () => {
    setPageState(prev => ({
      ...prev,
      isDonateDialogOpen: true,
      selectedTierId: undefined, // No specific tier selected
      donationSource: 'final_cta',
    }))

    trackEvent('donate_cta_clicked', {
      source: 'final_cta',
    })

    // Track dialog open
    trackEvent('donate_dialog_opened', {
      source: 'final_cta',
    })
  }

  /**
   * Close donation dialog
   */
  const handleCloseDialog = () => {
    setPageState(prev => ({
      ...prev,
      isDonateDialogOpen: false,
    }))

    trackEvent('donate_dialog_closed', {
      source: pageState.donationSource,
      selected_tier: pageState.selectedTierId,
    })
  }

  /**
   * Handle hero section CTA click - scroll to contribution tiers
   */
  const handleHeroCtaClick = () => {
    trackEvent('hero_cta_clicked', {
      location: 'donate_hero',
    })

    // Scroll to contribution tiers section
    if (contributionTiersRef.current) {
      contributionTiersRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }

  return (
    <ErrorBoundary>
      {/* SEO Meta Tags */}
      <SEOMetaTags
        title={seoConfig.title}
        description={seoConfig.description}
        canonical={canonicalUrl}
        ogImage={seoConfig.ogImage}
        keywords={seoConfig.keywords}
        noIndex={seoConfig.noIndex}
      />

      {/* Page Content with Transition Animation */}
      <PageTransition duration={300} enableSlide slideDirection="up">
        <Box component="main" sx={{ minHeight: '100vh' }}>
          {/* Hero Section */}
          <DonateHeroSection
            title={donatePageContent.hero.title}
            subtitle={donatePageContent.hero.subtitle}
            imageUrl={donatePageContent.hero.imageUrl}
            imageAlt={donatePageContent.hero.imageAlt}
            ctaText={donatePageContent.hero.ctaText}
            onCtaClick={handleHeroCtaClick}
          />

          {/* Emotional Narrative Section */}
          <EmotionalNarrativeSection
            title={donatePageContent.narrative.title}
            paragraphs={donatePageContent.narrative.paragraphs}
            mission={donatePageContent.narrative.mission}
          />

          {/* Fund Allocation Section */}
          <FundAllocationSection
            title={donatePageContent.fundAllocation.title}
            intro={donatePageContent.fundAllocation.intro}
            programs={
              donatePageContent.fundAllocation.programs as Array<{
                name: string
                description: string
                icon?:
                  | 'Event'
                  | 'Gavel'
                  | 'School'
                  | 'Handshake'
                  | 'FamilyRestroom'
              }>
            }
            conclusion={donatePageContent.fundAllocation.conclusion}
          />

          {/* Contribution Tiers Section */}
          <ContributionTiersSection
            ref={contributionTiersRef}
            title={donatePageContent.tiers.title}
            intro={donatePageContent.tiers.intro}
            tiers={activeTiers}
            onTierClick={handleTierClick}
            onTierView={handleTierView}
          />

          {/* Final CTA Section with 501(c)(3) Disclaimer */}
          <DonateCtaSection
            title={donatePageContent.finalCta.title}
            description={donatePageContent.finalCta.description}
            buttonText={donatePageContent.finalCta.buttonText}
            disclaimer={donatePageContent.disclaimer}
            onDonateClick={handleCtaClick}
          />
        </Box>
      </PageTransition>

      {/* Zeffy Donation Dialog */}
      <DonateDialog
        open={pageState.isDonateDialogOpen}
        onClose={handleCloseDialog}
        content={donationContent}
      />
    </ErrorBoundary>
  )
}
